import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../../lib/ui/components/Modal';
import Button from '../../lib/ui/components/button/Button';
import { Heading4, SmallText } from '../../lib/ui/components/typography/Typography';
import Spinner from '../../lib/ui/components/spinner/Spinner';
import { REJECTION_REASONS } from '../../lib/urls';

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-small text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-400';
const labelClass = 'mb-1 block text-small font-medium text-gray-700';

export default function RejectApplicantModal({
  isOpen,
  onClose,
  onConfirm,
  count,
  applicantName,
  companyId,
  token,
  isLoading,
}) {
  const [reasons, setReasons] = useState([]);
  const [fetchingReasons, setFetchingReasons] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [selectedReasonId, setSelectedReasonId] = useState('');
  const [note, setNote] = useState('');

  const fetchReasons = useCallback(async () => {
    setFetchingReasons(true);
    setFetchError(null);
    try {
      const response = await fetch(REJECTION_REASONS(companyId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error('Failed to load rejection reasons');
      }
      const data = await response.json();
      setReasons(data.data || []);
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setFetchingReasons(false);
    }
  }, [companyId, token]);

  useEffect(() => {
    if (isOpen) {
      setSelectedReasonId('');
      setNote('');
      fetchReasons();
    }
  }, [isOpen, fetchReasons]);

  const handleConfirm = () => {
    onConfirm({
      rejection_reason_id: selectedReasonId,
      note: note.trim() || null,
    });
  };

  const canConfirm = selectedReasonId && !isLoading;

  return (
    <Modal show={isOpen} onClose={onClose} size="lg" dismissible={!isLoading}>
      <div className="p-6">
        <Heading4 className="mb-4">
          Reject {count === 1 ? applicantName || 'Applicant' : `${count} Applicants`}
        </Heading4>

        <SmallText className="mb-6">
          {count === 1
            ? `This will reject ${applicantName || 'the applicant'} and notify the ATS. This action may trigger automated workflows (e.g., rejection email) in your ATS.`
            : `This will reject ${count} applicants and notify the ATS. This action may trigger automated workflows (e.g., rejection emails) in your ATS.`}
        </SmallText>

        <div className="mb-4">
          <label className={labelClass}>Rejection Reason *</label>
          {fetchingReasons ? (
            <div className="flex items-center gap-2 py-2">
              <Spinner size="sm" />
              <SmallText>Loading rejection reasons...</SmallText>
            </div>
          ) : fetchError ? (
            <div className="text-small text-red-600">
              {fetchError}
              <button
                type="button"
                className="ml-2 font-medium text-primary-700 underline hover:text-primary-800"
                onClick={fetchReasons}
              >
                Retry
              </button>
            </div>
          ) : (
            <select
              value={selectedReasonId}
              onChange={(e) => setSelectedReasonId(e.target.value)}
              disabled={reasons.length === 0}
              className={inputClass}
            >
              <option value="">Select a reason...</option>
              {reasons.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="mb-6">
          <label className={labelClass}>Note (optional)</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add additional details..."
            rows={3}
            className={inputClass}
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {isLoading
              ? 'Rejecting...'
              : count === 1
                ? 'Reject'
                : `Reject ${count} Applicants`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
